import axios from 'axios'
import i18next from 'i18next'
import th from './lang/th/th.json'
import en from './lang/en/en.json'
import cn from './lang/cn/cn.json'
import namespacesTH from './lang/th/namespace.json'
import namespacesEN from './lang/en/namespace.json'
import { Locale } from 'vant'
import vantEnUS from 'vant/es/locale/lang/en-US'
import vantThTH from 'vant/es/locale/lang/th-TH'
import vantZhCN from 'vant/es/locale/lang/zh-CN'
import { storage } from '@/utils'
import { useAppStore } from '@/stores/AppStore'

// mapping รหัสภาษาของแอป (th, en, cn) ไปยังรหัส locale ของ Vant
const VANT_LOCALE_KEY_MAP: Record<string, string> = {
  th: 'th-TH',
  en: 'en-US',
  cn: 'zh-CN',
}

// mapping ข้อความ locale ของ Vant สำหรับแต่ละภาษา
const VANT_LOCALE_MESSAGES: Record<string, typeof vantThTH> = {
  'th-TH': vantThTH,
  'en-US': vantEnUS,
  'zh-CN': vantZhCN,
}

// ค่า locale เริ่มต้นของ Vant (ใช้เมื่อไม่พบภาษาที่เลือก)
const VANT_DEFAULT_LOCALE_KEY = 'th-TH'

const INTL_LOCALE: Record<string, string> = {
  'th-TH': 'th-TH-u-ca-gregory',
  'en-US': 'en-US',
  'zh-CN': 'zh-CN',
}

function monthTitle(vantLocaleKey: string) {
  return (year: number, month: number) =>
    new Intl.DateTimeFormat(INTL_LOCALE[vantLocaleKey] ?? 'en-US', { month: 'long', year: 'numeric' }).format(
      new Date(year, month - 1),
    )
}

// ตั้งค่า locale ของ Vant ตามภาษาที่เลือก
function useVantLocale(lang: string) {
  const vantLocaleKey = VANT_LOCALE_KEY_MAP[lang] ?? VANT_DEFAULT_LOCALE_KEY
  const messages = VANT_LOCALE_MESSAGES[vantLocaleKey] ?? vantThTH
  Locale.use(vantLocaleKey, {
    ...messages,
    vanCalendar: { ...messages.vanCalendar, monthTitle: monthTitle(vantLocaleKey) },
  })
}

const XPRESS_DEFAULT_LANGUAGE = 'th'

export function getStoredLanguage(): string {
  return storage.getItem('language') || XPRESS_DEFAULT_LANGUAGE
}

async function applyLanguage(lang: string): Promise<void> {
  const appStore = useAppStore()
  appStore.setLanguage(lang)

  await i18next.changeLanguage(lang)
  useVantLocale(lang)
  window.localStorage.setItem('language', lang)
}

export function initAppLanguage(): void {
  const appStore = useAppStore()
  const lang = appStore.isXpress ? XPRESS_DEFAULT_LANGUAGE : getStoredLanguage()
  appStore.setLanguage(lang)
  if (!appStore.isXpress) syncLanguageFromServer()
}

export async function setPatientLanguage(lang: string): Promise<void> {
  const appStore = useAppStore()
  if (appStore.isXpress) return
  await applyLanguage(lang)
  await updateUserLanguageInMongoDB(lang)
}

export async function syncLanguageFromServer(): Promise<void> {
  const appStore = useAppStore()
  try {
    const lang = await getUserLanguageInMongoDB()
    if (lang && lang !== appStore.language) await applyLanguage(lang)
  } catch {}
}

export async function i18nInitialized({ ns }: { ns: string }): Promise<void> {
  const appStore = useAppStore()
  await i18next.init({
    lng: appStore.language,
    fallbackLng: { cn: ['en', 'th'], default: ['th'] },
    ns: ['translation', ...Object.keys(namespacesTH)],
    defaultNS: ns,
    fallbackNS: 'translation',
    interpolation: { escapeValue: false },
    resources: {
      th: { translation: th, ...namespacesTH },
      en: { translation: en, ...namespacesEN },
      cn: { translation: cn },
    },
  })
  useVantLocale(appStore.language)
}

async function updateUserLanguageInMongoDB(lang: string): Promise<void> {
  const appStore = useAppStore()
  try {
    await axios.post(`${appStore.crmUrl}/api/language`, {
      line_user_id: appStore.userProfile.userId,
      clinic_uuid: appStore.clinicUUID,
      language: lang,
    })
  } catch {}
}

async function getUserLanguageInMongoDB(): Promise<string> {
  const appStore = useAppStore()
  try {
    const res = await axios.get(`${appStore.crmUrl}/api/language`, {
      params: {
        line_user_id: appStore.userProfile.userId,
        clinic_uuid: appStore.clinicUUID,
      },
    })
    return res.data.language
  } catch {
    throw new Error('Cannot get language from line_crm')
  }
}
